import {
  type ClassId,
  getItem,
  type InventoryState,
  type StashRejected,
  type StashResult,
  type StashState,
} from '@shefira/shared';

import type { CssAtlas } from '../assets/dom-atlas.js';
import { stepper, upgradedName } from './bench.js';
import { closeButton } from './hud.js';
import { hideItemHover } from './item-hover.js';
import { kitHeading, kitTitle } from './kit.js';
import { StashGrid } from './stash-grid.js';
import { stashMoveBlock, type StashSide, type StashTarget } from './stash-moves.js';
import { uiFormat, uiNumber, uiText } from './text.js';
import { isTouch } from './touch.js';
import './smith.css';
import './bench.css';
import './stash.css';

/**
 * The account's chest (ADR 0079): its grid of cases beside the bag's, like
 * the inventory, side by side on a screen, a tab each on a phone. A drag, a
 * double-click, the bar under the grids or a touch card's buttons send
 * intents, one item, a count or the whole stack; the chest and the bag come
 * back from the server. Shared by every character of the account, at any
 * village's chest.
 */

export interface StashPanelOptions {
  icons: CssAtlas | null;
  /** The player: a piece's card judges his level and class. */
  wearer: () => { level: number; classId: ClassId };
  onDeposit: (itemId: string, quantity: number) => void;
  onWithdraw: (itemId: string, quantity: number) => void;
}

const REFUSALS: Record<StashRejected['reason'], string> = {
  invalid: 'Demande invalide.',
  'too-far': 'Approche-toi du coffre.',
  'unknown-item': 'Cet objet n’est plus là.',
  quantity: 'Tu n’en as pas autant.',
  bound: 'Cet objet est lié à ton personnage : le coffre ne prend pas les objets liés.',
  wardrobe: 'Les apparences et les compagnons restent dans ta garde-robe.',
  'stash-full': 'Le coffre est plein.',
  'bag-full': 'Ton sac est plein.',
  busy: 'Termine d’abord ton échange.',
};

export class StashPanel {
  private readonly root: HTMLElement;
  private readonly tabs: Record<StashSide, HTMLButtonElement>;
  private readonly heads: Record<StashSide, HTMLElement>;
  private readonly waits: Record<StashSide, HTMLElement>;
  private readonly grids: Record<StashSide, StashGrid>;
  private readonly far: HTMLElement;
  private readonly picked: HTMLElement;
  private readonly message: HTMLElement;
  /** The count chosen on a stack's stepper, by item id. */
  private readonly counts = new Map<string, number>();
  private inventory: InventoryState | undefined;
  private stash: StashState | undefined;
  private inReach = false;
  private readonly onKey = (event: KeyboardEvent): void => {
    if (event.key === 'Escape' && !this.root.hidden) this.close();
  };

  constructor(private readonly options: StashPanelOptions) {
    const grid = (side: StashSide, other: StashSide): StashGrid =>
      new StashGrid(side, {
        onMove: side === 'chest' ? options.onWithdraw : options.onDeposit,
        onReceive: (itemId) => {
          this.grids[other].moveWhole(itemId);
        },
        onSelect: () => {
          this.grids[other].unselect();
          this.renderPicked();
        },
      });
    this.grids = { chest: grid('chest', 'bag'), bag: grid('bag', 'chest') };

    this.root = document.createElement('section');
    this.root.className = 'smith stash';
    this.root.hidden = true;
    this.root.setAttribute('aria-label', uiText('Coffre'));

    const title = document.createElement('h2');
    title.className = 'smith__title';
    kitTitle(title, 'ach-chest');
    title.textContent = uiText('Coffre');
    title.append(
      closeButton('Fermer (Échap)', () => {
        this.close();
      }),
    );

    const lead = document.createElement('p');
    lead.className = 'bench__lead stash__lead';
    lead.textContent = uiText(
      'Partagé par tous les personnages de ton compte, dans chaque village.',
    );

    const tabBar = document.createElement('div');
    tabBar.className = 'stash__tabs';
    tabBar.setAttribute('role', 'tablist');
    const tab = (side: StashSide): HTMLButtonElement => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'stash__tab';
      button.setAttribute('role', 'tab');
      button.addEventListener('click', () => {
        this.setSide(side);
      });
      tabBar.append(button);
      return button;
    };
    this.tabs = { chest: tab('chest'), bag: tab('bag') };

    this.far = document.createElement('p');
    this.far.className = 'smith__warn stash__far';
    this.far.textContent = uiText('Approche-toi du coffre.');

    const columns = document.createElement('div');
    columns.className = 'stash__columns';
    const column = (side: StashSide): [HTMLElement, HTMLElement] => {
      const box = document.createElement('div');
      box.className = 'stash__column';
      box.dataset.side = side;
      const head = document.createElement('div');
      head.className = 'stash__heading';
      const wait = document.createElement('p');
      wait.className = 'bench__hint';
      wait.textContent = uiText('Le coffre s’ouvre…');
      box.append(head, this.grids[side].element, wait);
      columns.append(box);
      return [head, wait];
    };
    const [chestHead, chestWait] = column('chest');
    const [bagHead, bagWait] = column('bag');
    this.heads = { chest: chestHead, bag: bagHead };
    this.waits = { chest: chestWait, bag: bagWait };

    this.picked = document.createElement('div');
    this.picked.className = 'stash__picked';

    this.message = document.createElement('p');
    this.message.className = 'smith__message stash__message';
    this.message.setAttribute('aria-live', 'polite');

    this.root.append(title, lead, tabBar, this.far, columns, this.picked, this.message);
    document.body.append(this.root);
    window.addEventListener('keydown', this.onKey);
    this.setSide('chest');
  }

  get isOpen(): boolean {
    return !this.root.hidden;
  }

  /** Opens on the chest; its content follows from the server (`setStash`). */
  open(): void {
    this.root.hidden = false;
    this.stash = undefined;
    this.say('', 'success');
    this.setSide('chest');
  }

  close(): void {
    if (this.root.hidden) return;
    this.root.hidden = true;
    this.grids.chest.reset();
    this.grids.bag.reset();
    this.counts.clear();
    hideItemHover();
  }

  setInventory(state: InventoryState): void {
    this.inventory = state;
    this.render();
  }

  setStash(state: StashState): void {
    this.stash = state;
    this.render();
  }

  /** Whether the player stands by the chest; the buttons wait until they do. */
  setInReach(inReach: boolean): void {
    if (inReach === this.inReach) return;
    this.inReach = inReach;
    this.render();
  }

  showMoved(result: StashResult): void {
    const name = uiText(getItem(result.templateId)?.name ?? result.templateId);
    const what = result.quantity > 1 ? `${uiNumber(result.quantity)} × ${name}` : name;
    this.say(
      result.direction === 'deposit'
        ? uiFormat('Déposé au coffre : {what}.', { what })
        : uiFormat('Retiré du coffre : {what}.', { what }),
      'success',
    );
  }

  showRejected(rejection: StashRejected): void {
    this.say(uiText(REFUSALS[rejection.reason]), 'refused');
  }

  destroy(): void {
    window.removeEventListener('keydown', this.onKey);
    this.grids.chest.destroy();
    this.grids.bag.destroy();
    this.root.remove();
  }

  private setSide(side: StashSide): void {
    this.root.dataset.side = side;
    for (const [key, button] of Object.entries(this.tabs)) {
      const active = key === side;
      button.classList.toggle('stash__tab--active', active);
      button.setAttribute('aria-selected', String(active));
    }
    this.render();
  }

  private say(text: string, tone: 'success' | 'refused'): void {
    this.message.textContent = text;
    this.message.dataset.tone = tone;
  }

  private render(): void {
    const chest = this.stash;
    const bag = this.inventory;
    const chestCount = chest === undefined ? '…' : this.tally(chest.items.length, chest.capacity);
    const bagCount = bag === undefined ? '…' : this.tally(bag.bag.length, bag.bagSize);
    this.tabs.chest.textContent = `${uiText('Coffre')} ${chestCount}`;
    this.tabs.bag.textContent = `${uiText('Sac')} ${bagCount}`;
    this.heads.chest.replaceChildren(kitHeading('Coffre', chestCount));
    this.heads.bag.replaceChildren(kitHeading('Sac', bagCount));
    if (this.root.hidden) return;
    // The redraw takes the hovered item away without a mouseleave.
    hideItemHover();
    this.grids.chest.hide();
    this.grids.bag.hide();
    this.far.hidden = this.inReach;
    const ready = chest !== undefined && bag !== undefined;
    for (const side of ['chest', 'bag'] as const) {
      this.grids[side].element.hidden = !ready;
      this.waits[side].hidden = ready;
    }
    if (ready) {
      const wearer = this.options.wearer();
      this.grids.chest.render({
        icons: this.options.icons,
        entries: chest.items,
        capacity: chest.capacity,
        target: this.target('chest'),
        inReach: this.inReach,
        wearer,
      });
      this.grids.bag.render({
        icons: this.options.icons,
        entries: bag.bag,
        capacity: bag.bagSize,
        target: this.target('bag'),
        inReach: this.inReach,
        wearer,
      });
    }
    this.renderPicked();
  }

  /** Where one side's items would go: the other side's entries and size. */
  private target(side: StashSide): StashTarget {
    const chest = this.stash;
    const bag = this.inventory;
    if (chest === undefined || bag === undefined) return { entries: [], size: 0 };
    return side === 'chest'
      ? { entries: [...bag.bag, ...bag.wardrobe], size: bag.bagSize }
      : { entries: chest.items, size: chest.capacity };
  }

  /**
   * The bar under the grids: the picked item, why it stays, or the count of
   * a stack to send over (ADR 0079 lets part of a stack go).
   */
  private renderPicked(): void {
    const side: StashSide | null =
      this.grids.chest.pick !== null ? 'chest' : this.grids.bag.pick !== null ? 'bag' : null;
    const pick = side === null ? null : this.grids[side].pick;
    // A count kept for a stack that left: forgotten.
    for (const id of this.counts.keys()) if (id !== pick?.item.id) this.counts.delete(id);
    if (side === null || pick === null) {
      const hint = document.createElement('p');
      hint.className = 'bench__hint';
      hint.textContent = isTouch()
        ? uiText('Touche un objet pour le déposer ou le retirer.')
        : uiText('Glisse un objet d’une grille à l’autre ou double-clique dessus.');
      this.picked.replaceChildren(hint);
      return;
    }
    const { item, template } = pick;
    const name = document.createElement('span');
    name.className = 'stash__name';
    name.textContent = upgradedName(template, template.id, item.upgradeLevel);
    this.picked.replaceChildren(name);

    const target = this.target(side);
    const block = stashMoveBlock(side, item, template, item.quantity, target, true);
    if (block === 'bound' || block === 'wardrobe') {
      const why = document.createElement('span');
      why.className = 'stash__why';
      if (block === 'bound') {
        why.textContent = uiText('Lié : n’entre pas au coffre');
        why.title = uiText(
          'Lié à ce personnage : le coffre ne prend pas les objets liés, même ceux reçus par ton compte.',
        );
      } else {
        why.textContent = uiText('Reste dans la garde-robe.');
      }
      this.picked.append(why);
      return;
    }

    const most = item.quantity;
    const count = Math.min(most, Math.max(1, this.counts.get(item.id) ?? most));
    const onMove = side === 'chest' ? this.options.onWithdraw : this.options.onDeposit;
    const verb = side === 'chest' ? uiText('Retirer', 'stash') : uiText('Déposer');
    const button = (label: string, quantity: number, aria: string): HTMLButtonElement => {
      const go = document.createElement('button');
      go.type = 'button';
      go.className = 'smith__go stash__go';
      go.textContent = label;
      go.setAttribute('aria-label', aria);
      const refused = stashMoveBlock(side, item, template, quantity, target, this.inReach);
      go.disabled = refused !== null;
      if (refused === 'full') {
        go.title = side === 'chest' ? uiText('Ton sac est plein.') : uiText('Le coffre est plein.');
      }
      go.addEventListener('click', () => {
        onMove(item.id, quantity);
      });
      return go;
    };

    if (most > 1) {
      this.picked.append(
        stepper({
          count,
          most,
          onStep: (next) => {
            this.counts.set(item.id, Math.min(most, Math.max(1, next)));
            this.renderPicked();
          },
        }),
        button(verb, count, uiFormat('{verb} {count}', { verb, count: uiNumber(count) })),
      );
      if (count < most) {
        const all = button(uiText('Tout'), most, uiFormat('{verb} tout', { verb }));
        all.classList.add('stash__go--all');
        this.picked.append(all);
      }
    } else {
      this.picked.append(button(verb, most, verb));
    }
  }

  private tally(used: number, capacity: number): string {
    return `${uiNumber(used)} / ${uiNumber(capacity)}`;
  }
}
